import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../../components/ui/Dialog";
import {
  useApproveLeave,
  useRejectLeave,
  useCancelLeave,
} from "../../../../api/queries/leaveQueries";
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from "../../../../utils/apiError";
import { employeeName } from "../../../../utils/text";
import { changeLabel, formatDays, formatLeaveDates } from "./leaveFormat";
import LeaveChangeDetails from "./LeaveChangeDetails";
import usePermissions from "../../../../hooks/usePermissions";

const REMARKS_MAX = 500;

// " for Juan Dela Cruz", or "" when the row has no employee (per-employee lists)
const forWhom = (request) =>
  request.employee ? ` for ${employeeName(request.employee)}` : "";

/**
 * Shared error handling for approve / reject / cancel:
 * - 409 (already handled / changed by someone else): toast, close (lists refetch)
 * - 403 (own request / out of scope / not your level yet): close, disable that row's buttons
 *   (the global 403 handler shows the server message)
 * - anything else (e.g. 400): show inside the dialog
 */
const useActionErrorHandler = ({ request, onClose, onForbidden, setError }) =>
  useCallback(
    (err) => {
      const status = getApiErrorStatus(err);
      const message = getApiErrorMessage(err, "Something went wrong");
      if (status === 409) {
        toast.info(message);
        onClose();
      } else if (status === 403) {
        onForbidden?.(request.id);
        onClose();
      } else {
        setError(message);
      }
    },
    [request, onClose, onForbidden, setError],
  );

/** Who / what / when, shown at the top of each dialog. */
export const LeaveSummary = ({ request }) => (
  <div className="leave-summary">
    {request.employee && (
      <div className="fw-semibold text-navy">
        {employeeName(request.employee)}
        {request.employee.employeeId && (
          <span className="text-slate-500 fw-normal fs-080 ms-2">
            {request.employee.employeeId}
          </span>
        )}
      </div>
    )}
    <div className="fs-080 text-slate-600">
      {request.leaveType?.name || "Leave"} ·{" "}
      {formatLeaveDates(request.startDate, request.endDate)} ·{" "}
      {formatDays(request.totalDays)}
    </div>
  </div>
);

const RemarksField = ({
  id,
  label,
  value,
  onChange,
  required,
  placeholder,
  autoFocus,
}) => (
  <div>
    <label htmlFor={id} className="profile-label">
      {label}{" "}
      {required ? (
        <span className="text-danger">*</span>
      ) : (
        <span className="text-slate-400 fw-normal">(optional)</span>
      )}
    </label>
    <textarea
      id={id}
      className="form-control"
      rows={3}
      maxLength={REMARKS_MAX}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      required={required}
    />
    <div className="text-end text-slate-400 fs-070 mt-1">
      {value.length}/{REMARKS_MAX}
    </div>
  </div>
);

const Footer = ({ onClose, busy, onSubmit, disabled, label, variant }) => (
  <>
    <button
      type="button"
      className="btn btn-outline-secondary profile-btn"
      onClick={onClose}
      disabled={busy}
    >
      Close
    </button>
    <button
      type="button"
      className={`btn btn-${variant} profile-btn`}
      onClick={onSubmit}
      disabled={busy || disabled}
    >
      {busy && (
        <span
          className="spinner-border spinner-border-sm"
          aria-hidden="true"
        ></span>
      )}
      {label}
    </button>
  </>
);

const ErrorAlert = ({ error }) =>
  error ? (
    <div className="alert alert-danger fs-085 py-2" role="alert">
      {error}
    </div>
  ) : null;

export const ApproveLeaveDialog = ({ request, onClose, onForbidden }) => {
  // HR-level (leave with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("leave");
  const change = request.pendingChange;
  // A cancel / edit request is one decision, whatever the levels
  const levels = change ? null : request.levels;
  const remaining = levels?.total > 1 ? levels.total - levels.current + 1 : 1;
  const [allLevels, setAllLevels] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState(null);
  const approve = useApproveLeave();
  const onError = useActionErrorHandler({
    request,
    onClose,
    onForbidden,
    setError,
  });

  const submit = () => {
    setError(null);
    approve.mutate(
      {
        id: request.id,
        remarks: remarks.trim() || undefined,
        allLevels: allLevels || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            change === "CANCEL"
              ? `Leave cancelled${forWhom(request)}; the days are back in the balance.`
              : change === "EDIT"
                ? `Leave updated${forWhom(request)}.`
                : remaining > 1 && !allLevels
                  ? `Level ${levels.current} approved${forWhom(request)}; sent to the next level.`
                  : `Leave approved${forWhom(request)}.`,
          );
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title={
        change
          ? `Approve ${changeLabel(change).toLowerCase()}?`
          : "Approve leave?"
      }
      onClose={onClose}
      busy={approve.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={approve.isPending}
          onSubmit={submit}
          label="Approve"
          variant="success"
        />
      }
    >
      <ErrorAlert error={error} />
      <LeaveSummary request={request} />
      {change && <LeaveChangeDetails request={request} />}
      {levels?.total > 1 && (
        <p className="fs-080 text-slate-600">
          Level {levels.current} of {levels.total}.{" "}
          {remaining > 1
            ? "After you, it goes to the next level."
            : "This is the last level."}
        </p>
      )}
      <RemarksField
        id="approve-remarks"
        label="Remarks"
        value={remarks}
        onChange={setRemarks}
        placeholder="e.g. Enjoy your break"
        autoFocus
      />
      {isCompanyWide && remaining > 1 && (
        <div className="form-check">
          <input
            id="leave-all-levels"
            type="checkbox"
            className="form-check-input"
            checked={allLevels}
            onChange={(e) => setAllLevels(e.target.checked)}
          />
          <label htmlFor="leave-all-levels" className="form-check-label fs-080">
            Approve all remaining levels ({remaining}) as HR
          </label>
        </div>
      )}
    </Dialog>
  );
};

export const RejectLeaveDialog = ({ request, onClose, onForbidden }) => {
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState(null);
  const reject = useRejectLeave();
  const change = request.pendingChange;
  const onError = useActionErrorHandler({
    request,
    onClose,
    onForbidden,
    setError,
  });
  const isEmpty = !remarks.trim();

  const submit = () => {
    if (isEmpty) return;
    setError(null);
    reject.mutate(
      { id: request.id, remarks: remarks.trim() },
      {
        onSuccess: () => {
          toast.success(
            change
              ? `${changeLabel(change)} rejected${forWhom(request)}; ${
                  request.status === "approved"
                    ? "the leave stays as approved."
                    : "the request still needs its normal approval."
                }`
              : `Leave rejected${forWhom(request)}.`,
          );
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title={
        change
          ? `Reject ${changeLabel(change).toLowerCase()}?`
          : "Reject leave?"
      }
      subtitle={
        change
          ? request.status === "approved"
            ? "The leave stays as approved. The employee will see your reason."
            : "The request stays pending for its normal approval. The employee will see your reason."
          : "The employee will see your reason."
      }
      onClose={onClose}
      busy={reject.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={reject.isPending}
          onSubmit={submit}
          disabled={isEmpty}
          label="Reject"
          variant="danger"
        />
      }
    >
      <ErrorAlert error={error} />
      <LeaveSummary request={request} />
      {change && <LeaveChangeDetails request={request} />}
      <RemarksField
        id="reject-remarks"
        label="Reason"
        required
        value={remarks}
        onChange={setRemarks}
        placeholder="e.g. Peak season; please choose other dates"
        autoFocus
      />
    </Dialog>
  );
};

export const CancelLeaveDialog = ({ request, onClose, onForbidden }) => {
  const [error, setError] = useState(null);
  const cancel = useCancelLeave();
  const onError = useActionErrorHandler({
    request,
    onClose,
    onForbidden,
    setError,
  });
  const wasApproved = request.status === "approved";

  const submit = () => {
    setError(null);
    cancel.mutate(
      { id: request.id },
      {
        onSuccess: () => {
          toast.success(`Leave cancelled${forWhom(request)}.`);
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog
      title={wasApproved ? "Cancel approved leave?" : "Cancel leave request?"}
      onClose={onClose}
      busy={cancel.isPending}
      footer={
        <Footer
          onClose={onClose}
          busy={cancel.isPending}
          onSubmit={submit}
          label="Cancel leave"
          variant="warning"
        />
      }
    >
      <ErrorAlert error={error} />
      <LeaveSummary request={request} />
      <p className="fs-085 mb-0">
        <i className="bi bi-arrow-counterclockwise me-1 text-slate-500"></i>
        This returns the {formatDays(request.totalDays)} to the employee's
        balance.
      </p>
    </Dialog>
  );
};

const DIALOGS = {
  approve: ApproveLeaveDialog,
  reject: RejectLeaveDialog,
  cancel: CancelLeaveDialog,
};

/**
 * One place to open approve / reject / cancel dialogs.
 * Returns { open(type, request), isOpen, dialog, isForbidden(id) }; render `dialog`.
 * Requests that came back 403 stay disabled for this visit.
 */
export const useLeaveReviewDialogs = () => {
  const [active, setActive] = useState(null); // { type, request }
  const [forbidden, setForbidden] = useState(() => new Set());

  const close = useCallback(() => setActive(null), []);
  const markForbidden = useCallback(
    (id) => setForbidden((prev) => new Set(prev).add(id)),
    [],
  );

  const Component = active ? DIALOGS[active.type] : null;
  return {
    open: (type, request) => setActive({ type, request }),
    isOpen: !!active,
    isForbidden: (id) => forbidden.has(id),
    dialog: Component ? (
      <Component
        request={active.request}
        onClose={close}
        onForbidden={markForbidden}
      />
    ) : null,
  };
};
